import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Tag, User, ArrowRight } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { company } from "../data/mock";
import { API } from "../lib/auth";
import Seo from "../components/Seo";

const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    axios.get(`${API}/blog/${slug}`)
      .then((res) => { setPost(res.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading article...</div>;
  }

  if (status === "notfound") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Article not found</h1>
        <p className="text-slate-500">This post may have been moved or unpublished.</p>
        <Link to="/blog"><Button className="brand-gradient text-white font-bold"><ArrowLeft className="mr-2 h-4 w-4" />Back to blog</Button></Link>
      </div>
    );
  }

  return (
    <article className="bg-white">
      <Seo
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        image={post.cover_image}
        type="article"
      />
      {/* Header */}
      <div className="navy-gradient">
        <div className="mx-auto max-w-3xl px-6 pt-14 pb-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-blue-200 hover:text-white transition-colors text-sm font-semibold">
            <ArrowLeft className="h-4 w-4" /> Back to all articles
          </Link>
          <Badge className="mt-6 bg-white/15 text-white hover:bg-white/15 border border-white/20 font-semibold">
            <Tag className="h-3 w-3 mr-1" />{post.category}
          </Badge>
          <h1 className="mt-4 font-display text-3xl md:text-4xl font-extrabold text-white leading-tight">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-blue-100/80">
            <span className="flex items-center gap-2"><User className="h-4 w-4" /> {post.author}</span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {fmtDate(post.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Cover */}
      <div className="mx-auto max-w-3xl px-6 -mt-6">
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200">
          <img src={post.cover_image} alt={post.title} className="w-full h-64 md:h-80 object-cover" />
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-3xl px-6 py-12">
        <p className="text-xl text-slate-700 font-medium leading-relaxed mb-8">{post.excerpt}</p>
        <div className="prose-content space-y-5">
          {post.content.split("\n\n").map((para, i) => (
            <p key={i} className="text-lg text-slate-700 leading-relaxed">{para}</p>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-3xl navy-gradient p-8 md:p-10 text-center relative overflow-hidden">
          <div className="blob h-48 w-48 bg-blue-500 -top-8 right-0" />
          <div className="relative z-10">
            <h3 className="font-display text-2xl font-bold text-white">Want results like these for your business?</h3>
            <p className="mt-2 text-blue-100/80">Book a free strategy call and let's build your growth plan.</p>
            <a href={company.bookingUrl} target="_blank" rel="noreferrer">
              <Button size="lg" className="mt-6 bg-white text-blue-700 hover:bg-blue-50 font-bold group">
                Schedule a Call Today
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPostPage;
